import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import React, { useState } from 'react';

import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv/index';
import { IContentHubItem } from '@/components/sections/content-hub/gallery/item';
import { ModalContentHub } from '@/components/sections/content-hub/gallery/modal';
import Detail from '@/components/sections/content-hub/grid/detail';
import { removeHTMLTag } from '@/components/shared/utility/format';
import { NOT_FOUND_IMAGE } from '@/contents/contenthub';
import { MENU_LCV_CONTENT_HUB } from '@/contents/lcv/link';
import {
  getLCVContentHub,
  getLCVContentHubByURL,
  getLCVContentHubCategory,
  getLCVContentHubRelated,
} from '@/services/contentstack';

import { getCategoryList } from '..';

const RelateItems = dynamic(
  () => import('@/components/sections/content-hub/related'),
  {
    ssr: false,
  }
);

const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);

export const parseContentHubItems = (items): IContentHubItem[] => {
  const _items = items.map((o) => {
    let IMG, IMG_DETAIL;
    if (o.image_thumb && o.image_thumb.dimension) {
      IMG = {
        src: o.image_thumb ? o.image_thumb.url : '',
        alt: o.name,
        width: o.image_thumb.dimension.width,
        height: o.image_thumb.dimension.height,
      };
    } else {
      IMG = NOT_FOUND_IMAGE;
    }
    if (o.image_detail && o.image_detail.dimension) {
      IMG_DETAIL = {
        src: o.image_detail ? o.image_detail.url : '',
        alt: o.name,
        width: o.image_detail.dimension.width,
        height: o.image_detail.dimension.height,
      };
    } else {
      IMG_DETAIL = NOT_FOUND_IMAGE;
    }

    return {
      uid: o.uid,
      metatag: o.metatag,
      beginDate: o.begin_date,
      updated_at: o.updated_at || o.created_at,
      pin: o?.pin ?? false,
      endDate: o.end_date,
      tag: o.tag.map((c) => ({
        uid: c.uid,
        name: c.name,
        url: c.url,
      })),
      url: `${MENU_LCV_CONTENT_HUB.url}/${o.category[0].url}/${o.url}`,
      title: o.name,
      author: o.author,
      excerpt: o.excerpt,
      detail_title: o.detail_title ? o.detail_title : '',
      detail: o.detail ? o.detail : '',
      image: IMG,
      imageDetail: IMG_DETAIL,
      youtube_id: o.youtube_id ? o.youtube_id : '',
      buttons: o.buttons ? o.buttons.map((o) => o.button) : [],
      album_title: o.album_title ? o.album_title : '',
      albums: o.albums
        ? o.albums.map((o) => ({
            src: o.album.url,
            alt: o.album.title,
            width: o.album.dimension.width,
            height: o.album.dimension.height,
          }))
        : [],
    };
  });

  return _items;
};

export async function getStaticPaths() {
  const res = await getLCVContentHub();

  const entries = res.data.entries;

  const paths = entries.map((item) => ({
    params: {
      url: item.url,
      category: item.category[0].url,
    },
  }));

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params }) {
  const res = await getLCVContentHubByURL(params.url);
  const items = parseContentHubItems(res.data.entries);

  if (!items.length) {
    return {
      redirect: {
        destination: `${MENU_LCV_CONTENT_HUB.url}/${params.category}`,
      },
    };
  }
  const resCategory = await getLCVContentHubCategory();
  resCategory.data.entries = resCategory.data.entries.map((o) => ({
    name: o.name,
    url: o.url,
    metatag: {
      title: o.metatag && o.metatag.title,
      description: o.metatag && o.metatag.description,
      img: {
        url: o.metatag.img && o.metatag.img.url,
      },
    },
    headline: {
      title: o.headline && o.headline.headline_title,
      description: o.headline && o.headline.headline_description,
      buttons: o.headline && o.headline.headline_button,
    },
  }));

  const tags = items[0].tag.map((o) => o.url);

  let relatedItems: IContentHubItem[] = [];
  if (params.category == 'expert') {
    const resRelated = await getLCVContentHubRelated({
      url: params.url,
      category: params.category,
      tags: tags,
    });
    relatedItems = parseContentHubItems(resRelated.data.entries);
  }

  return {
    props: JSON.parse(
      JSON.stringify({
        item: items[0],
        relateItems: relatedItems,
        category: getCategoryList(resCategory.data.entries, params.category),
        currentCategory: params.category,
      })
    ),
  };
}

interface IPageProps {
  item: IContentHubItem;
  relateItems: IContentHubItem[];
  category?: {
    name: string;
    url: string;
    active: boolean;
  }[];
  currentCategory: string;
}

export default function Page(props: IPageProps) {
  const [isOpenModal, setIsOpenModal] = useState<boolean>(true);

  const metatag = {
    title: props.item.metatag.title
      ? props.item.metatag.title
      : props.item.title,
    description: props.item.metatag
      ? props.item.metatag.description
      : removeHTMLTag(props.item.detail),
    imgURL:
      props.item.metatag.img && props.item.metatag.img.url
        ? props.item.metatag.img.url
        : props.item.imageDetail.src,
  };

  return (
    <>
      <CustomMetatag {...metatag} />
      <main>
        {props.currentCategory === 'expert' ? (
          <>
            <Detail {...props.item} category={props.category ?? []} />
            {!!props.relateItems && props.relateItems.length > 0 && (
              <RelateItems items={props.relateItems} />
            )}
          </>
        ) : (
          <ModalContentHub
            initial
            initialURL={`${MENU_LCV_CONTENT_HUB.url}/${props.currentCategory}`}
            items={[props.item]}
            open={isOpenModal}
            onClose={() => setIsOpenModal(false)}
          />
        )}
      </main>
      <FooterLCVGroup />
    </>
  );
}

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
